/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import React, { Fragment } from "react";
import { FeatureEnabled } from "tests/FeatureEnabled";

import { KEYWORD_MAX_COUNT } from "../../constants/keywordMaxCount";
import { Checkbox } from "./components/Checkbox";
import { CheckboxField } from "./components/CheckboxField";
import { DateInput } from "./components/DateInput";
import { Field } from "./components/Field";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { PrefecturesSelector } from "./components/PrefecturesSelector";
import {
  bodyStyle,
  checkboxLabelTextStyle,
  checkboxesWrapperStyle,
  dateRangeContainerStyle,
  hyphenStyle,
  inputStyle,
  modalStyle,
  overlayStyle,
  rootStyles,
  wrapperForDatePicker,
} from "./styles";
import { useThis } from "./useThis";

import type { Props } from "./props";

export const DetailedSearchModal: React.FC<Props> = (props) => {
  const { isDetailSearchEnabled, keyword, onChangeKeyword, onClose } = props;
  const {
    canSearch,
    commonConditions,
    endDate,
    endMinDate,
    handleCheckArea,
    handleCheckPrefecture,
    handleCollapseArea,
    handleOverlayClick,
    handleSearch,
    handleSelectEndDate,
    handleSelectStartDate,
    inputRef,
    keywordInputTitle,
    prefectures,
    resultCount,
    startDate,
    startMaxDate,
    t,
  } = useThis(props);

  // TODO: goods 全公開前までの一時的な対応
  //  goods 公開前後で親要素の有無が変わるため、内部を共通化しておく
  const rangeDatePicker = (
    <div css={dateRangeContainerStyle}>
      <DateInput
        selectedDate={startDate}
        maxDate={startMaxDate}
        onSelectDate={handleSelectStartDate}
        placeholder={t("search.searchModal.searchConditions.eventDate.addDate")}
        position="left"
      />
      <span css={hyphenStyle}>-</span>
      <DateInput
        selectedDate={endDate}
        minDate={endMinDate}
        onSelectDate={handleSelectEndDate}
        placeholder={t("search.searchModal.searchConditions.eventDate.addDate")}
        position="right"
      />
    </div>
  );

  return (
    <div css={rootStyles}>
      <form css={modalStyle} onSubmit={handleSearch}>
        <Header onClose={onClose} />

        <div css={bodyStyle}>
          <Field legendTitle={t("search.searchModal.searchIndexes.keyword")}>
            <input
              ref={inputRef}
              aria-label={t(
                "search.searchModal.searchConditions.keyword.placeholder"
              )}
              css={inputStyle}
              maxLength={KEYWORD_MAX_COUNT}
              onChange={onChangeKeyword}
              placeholder={t(
                "search.searchModal.searchConditions.keyword.placeholder"
              )}
              title={keywordInputTitle}
              type="text"
              value={keyword}
            />
          </Field>

          <Field legendTitle={t("search.searchModal.searchIndexes.eventDate")}>
            {isDetailSearchEnabled ? (
              rangeDatePicker
            ) : (
              <div css={wrapperForDatePicker}>{rangeDatePicker}</div>
            )}
          </Field>

          {isDetailSearchEnabled && (
            <Fragment>
              {commonConditions.map((input) => {
                const { commonCondition, handleCheck } = input;
                const { categoryLabel, checkboxData } = commonCondition;

                return (
                  <Field key={categoryLabel} legendTitle={categoryLabel}>
                    <div css={checkboxesWrapperStyle}>
                      {checkboxData.map((categoryItem) => {
                        const { checkboxId, checkboxLabel, isChecked } =
                          categoryItem;
                        const onCheck = () => {
                          handleCheck(checkboxId);
                        };

                        return (
                          <CheckboxField
                            key={checkboxId}
                            checkboxElement={
                              <Checkbox
                                checked={isChecked}
                                onChange={onCheck}
                              />
                            }
                            labelText={
                              <span css={checkboxLabelTextStyle}>
                                {checkboxLabel}
                              </span>
                            }
                          />
                        );
                      })}
                    </div>
                  </Field>
                );
              })}

              <FeatureEnabled
                featureComponent={
                  <PrefecturesSelector
                    groupedPrefectures={prefectures}
                    onCheckArea={handleCheckArea}
                    onCheckPrefecture={handleCheckPrefecture}
                    onCollapseArea={handleCollapseArea}
                  />
                }
                featureName="fanySearch"
              />
            </Fragment>
          )}
        </div>

        <Footer
          canSearch={canSearch}
          onClose={onClose}
          resultCount={resultCount}
        />
      </form>
      <div css={overlayStyle} onClick={handleOverlayClick} />
    </div>
  );
};
